/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Box, Button, Divider, Typography, CircularProgress, useTheme } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchCategories,
  fetchCollections,
  fetchGetProductById,
  fetchTags,
  fetchDeleteProduct,
} from "../../store/reducers/createProfile/apithunk";
import { ToastNotifyError, ToastNotifySuccess } from "../../components/CommonComponents/Toast/ToastNotify";
import ProductDeleteModal from "./ProductDeleteModal";
import rollbar from "../../rollbarConfig";
import { processRichTextContent } from "../../utils/richTextUtils";
import wanderobelogo from "../../assets/images/WanderobeLogoNoImagePlaceholder.svg";
import { ROUTE_PATH } from "../../constants/route";
import productDetailStyles from "./ProductDetail.styles";

const ProductDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const styles = productDetailStyles(theme);
  
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [collections, setCollections] = useState([]);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [imageLoading, setImageLoading] = useState(true);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const getCollections = async () => {
      try {
        const res = await dispatch(fetchCollections());
        const { data, status, message } = res?.payload;
        if (status) {
          setCollections(data);
        } else {
          ToastNotifyError(message);
        }
      } catch (error) {
        console.error("Error fetching collections:", error);
        rollbar.error("Error message", error);
      }
    };

    getCollections();
  }, []);

  useEffect(() => {
    const getTags = async () => {
      try {
        const res = await dispatch(fetchTags());
        const { data, status, message } = res?.payload;
        if (status) {
          setTags(data);
        } else {
          ToastNotifyError(message);
        }
      } catch (error) {
        console.error("Error fetching tags:", error);
        rollbar.error("Error message", error);
      }
    };

    getTags();
  }, []);

  useEffect(() => {
    const getCategories = async () => {
      try {
        const res = await dispatch(fetchCategories());
        const { data, status, message } = res?.payload;
        if (status) {
          setCategories(data);
        } else {
          ToastNotifyError(message);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        rollbar.error("Error message", error);
      }
    };

    getCategories();
  }, []);

  const getProduct = async (productId) => {
    try {
      const res = await dispatch(fetchGetProductById(productId));
      if (res?.payload?.status === 404) {
        ToastNotifyError(res?.payload?.response?.data?.detail || "Product not found");
        navigate(ROUTE_PATH.INVENTORY_SETUP);
        return;
      }
      const { data, status, message } = res?.payload;
      if (status) {
        setSelectedProduct(data);
      } else {
        ToastNotifyError(message);
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      rollbar.error("Error message", error);
    }
  };

  useEffect(() => {
    if (id) {
      getProduct(id);
    }
  }, [id]);

  const handleBack = () => {
    navigate(ROUTE_PATH.INVENTORY_SETUP);
  };

  const handleEdit = () => {
    navigate(`/inventory-setup/edit-product/${id}`);
  };

  const handleDelete = () => {
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!id) return;

    try {
      const res = await dispatch(fetchDeleteProduct(id));
      const { status, message } = res?.payload || {};
      
      if (status) {
        ToastNotifySuccess(message || "Product deleted successfully!");
        setOpenDeleteModal(false);
        navigate(ROUTE_PATH.INVENTORY_SETUP);
      } else {
        ToastNotifyError(message || "Failed to delete product");
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      rollbar.error("Error deleting product", error);
      ToastNotifyError("Failed to delete product");
    }
  };

  const selectedStatus = (status) => {
    if (status === "A") {
      return "Active";
    } else if (status === "In") {
      return "InActive";
    }
    return status;
  };

  const selectedCategories = (productCategories) => {
    return categories
      ?.map((category) =>
        productCategories?.includes(category?.id) ? category : null
      )
      ?.filter(Boolean);
  };

  const selectedCollections = (productCollection) => {
    return collections?.find(
      (collection) => collection?.id === productCollection
    );
  };

  const selectedTags = (productTags) => {
    return tags
      ?.map((tag) =>
        productTags?.includes(tag?.id) ? tag : null
      )
      ?.filter(Boolean);
  };

  const formatVariantOptions = (variant) => {
    if (!variant?.options) return { colorPart: "", otherParts: "" };
    
    const options = variant?.options;
    
    // Separate COLOR from other options
    const colorKey = Object.keys(options)?.find(key => key?.toUpperCase() === "COLOR");
    const otherOptions = Object.keys(options)?.filter(key => key?.toUpperCase() !== "COLOR");
    
    let colorPart = "";
    let otherParts = "";
    
    // Add COLOR first if it exists
    if (colorKey) {
      const colorValue = options?.[colorKey];
      colorPart = `COLOR: ${colorValue}`;
    }
    
    // Add other options
    otherOptions?.forEach((key) => {
      if (otherParts) {
        otherParts += " / ";
      }
      otherParts += `${key}: ${options?.[key]}`;
    });
    
    return { colorPart, otherParts };
  };

  const getColorHexCode = (variant) => {
    if (!variant?.option_values_with_hex) return null;
    
    const colorKey = Object.keys(variant?.option_values_with_hex)?.find(
      key => key.toUpperCase() === "COLOR"
    );
    
    return colorKey ? variant?.option_values_with_hex?.[colorKey]?.hex_code : null;
  };

  return (
    <Box sx={{ padding: 0 }}>
      <Box sx={styles.container}>
        <Box sx={styles.headerWrap}>
          <Box sx={styles.backBtn} onClick={handleBack}>
            <ArrowBackIcon />
            <Typography sx={styles.title}>Product Details</Typography>
          </Box>
          <Box sx={styles.editBtn} onClick={handleEdit}>
            <EditOutlinedIcon />
            <Typography sx={styles.editText}>Edit</Typography>
          </Box>
        </Box>
        <Box sx={styles.productInformation}>
          <Typography sx={styles.subHeading}>Product Information</Typography>
          <Box sx={styles.productInformationData}>
            <Box sx={styles.imageWrap}>
              <Box sx={styles.imageContainer}>
                <Box sx={styles.productImage}>
                  {imageLoading && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        width: "100%",
                        position: "absolute",
                      }}
                    >
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  {selectedProduct?.variants?.[0]?.variant_img?.[0] || selectedProduct?.images?.[0] ? (
                  <img
                    src={
                      selectedProduct?.variants?.[0]?.variant_img?.[0] ||
                      selectedProduct?.images?.[0] ||
                      wanderobelogo
                    }
                    alt="product-image"
                    style={{ opacity: imageLoading ? 0 : 1 }}
                    onLoad={() => setImageLoading(false)}
                  />
                  ) : (
                    <Box
                      component='img'
                      src={wanderobelogo}
                      alt={selectedProduct?.name}
                      sx={{ height: 40, width: 40, opacity: 0.3, objectFit: "contain", border: "0.62px solid #CBCBCB" }}
                      onLoad={() => setImageLoading(false)}
                    />
                  )}
                </Box>
                <Typography sx={styles.imageTitle}>
                  {selectedProduct?.name}
                </Typography>
              </Box>
              <Box sx={styles.statusWrap}>
                <Typography sx={styles.statusTitle}>Status</Typography>
                <Typography sx={styles.statusValue}>
                  {selectedStatus(selectedProduct?.status)}
                </Typography>
              </Box>
            </Box>
            <Box sx={styles.description}>
              <Typography sx={styles.descriptionTitle}>Description</Typography>
              <Box
                sx={styles.descriptionValue}
                dangerouslySetInnerHTML={{
                  __html: processRichTextContent(selectedProduct?.description, {
                    imageMaxHeight: "250px",
                    videoMaxHeight: "250px",
                    iframeMaxHeight: "250px",
                    containerMaxWidth: "100%",
                  }),
                }}
              />
            </Box>
            <Box>
              <Typography sx={styles.commonTitle}>Collection</Typography>
              <Box sx={styles.chipWrap}>
                <Box sx={styles.chip}>
                  <Typography sx={styles.chipValue}>
                    {selectedCollections(selectedProduct?.collection)?.name}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box>
              <Typography sx={styles.commonTitle}>Categories</Typography>
              <Box sx={styles.chipWrap}>
                {selectedCategories(selectedProduct?.categories)?.map(
                  (item, index) => (
                    <Box key={index} sx={styles.chip}>
                      <Typography sx={styles.chipValue}>{item?.name}</Typography>
                    </Box>
                  )
                )}
              </Box>
            </Box>
            <Box>
              <Typography sx={styles.commonTitle}>Tags</Typography>
              <Box sx={styles.chipWrap}>
                {selectedTags(selectedProduct?.tags)?.map((item, index) => (
                  <Box key={index} sx={styles.chip}>
                    <Typography sx={styles.chipValue}>{item?.name}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Box sx={styles.boxWrap}>
              <Typography sx={styles.commonTitle2}>
                PRICING & QUANTITY
              </Typography>
              <Box sx={styles.textWrap}>
                <Box sx={styles.text}>
                  <Typography sx={styles.commonTitle3}>Price (in USD):</Typography>
                  <Typography sx={styles.commonValue3}>
                    {`$${selectedProduct?.price || 0}`}
                  </Typography>
                </Box>
                <Box sx={styles.text}>
                  <Typography sx={styles.commonTitle3}>Total Qty:</Typography>
                  <Typography sx={styles.commonValue3}>
                    {selectedProduct?.quantity}
                  </Typography>
                </Box>
              </Box>
            </Box>
            {(() => {
              const hasVariant = selectedProduct?.has_variant !== false;
              const firstVariantHasShopifyId = selectedProduct?.variants?.[0]?.shopify_variant_id;
              const shouldShowVariants = hasVariant && !firstVariantHasShopifyId && selectedProduct?.variants?.length > 0;
              
              if (!shouldShowVariants) {
                return null;
              }
              
              return (
                <Box sx={styles.boxWrap}>
                  <Typography sx={styles.commonTitle2}>PRODUCT VARIANT</Typography>
                  {selectedProduct?.variants?.map((item, index) => (
                <React.Fragment key={index}>
                  <Box sx={styles.textWrap}>
                    <Box sx={styles.text}>
                      <Typography sx={styles.commonTitle3}>Qty.:</Typography>
                      <Typography sx={styles.commonValue3}>
                        {item?.quantity || 0}
                      </Typography>
                    </Box>
                    <Box sx={styles.textWrap2}>
                      {(() => {
                        const formattedOptions = formatVariantOptions(item);
                        const hasColor = formattedOptions?.colorPart !== "";
                        const hasOtherOptions = formattedOptions?.otherParts !== "";
                        const colorHexCode = getColorHexCode(item);
                        
                        if (!formattedOptions?.colorPart && !formattedOptions?.otherParts) {
                          return (
                            <Typography sx={styles.commonValue4}>
                              {item?.variant_title || ""}
                            </Typography>
                          );
                        }
                        
                        return (
                          <>
                            {hasColor && (
                              <>
                                <Typography sx={styles.commonValue4} component="span">
                                  {formattedOptions?.colorPart}
                                </Typography>
                                {colorHexCode && (
                                  <Box
                                    sx={{
                                      ...styles.colorBox,
                                      backgroundColor: colorHexCode,
                                      marginLeft: "8px",
                                      marginRight: hasOtherOptions ? "8px" : "0",
                                      display: "inline-block",
                                    }}
                                  />
                                )}
                              </>
                            )}
                            {hasOtherOptions && (
                              <Typography sx={styles.commonValue4} component="span">
                                {hasColor && " / "}
                                {formattedOptions?.otherParts}
                              </Typography>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  </Box>
                  {index !== selectedProduct?.variants?.length - 1 && (
                    <Divider sx={{ width: "50%", height: "1px" }} />
                  )}
                </React.Fragment>
              ))}
                </Box>
              );
            })()}
          </Box>
        </Box>
        <Box sx={styles.deleteBtnContainer}>
          <Button
            variant="outlined"
            sx={styles.deleteBtn}
            onClick={handleDelete}
          >
            Delete Product
          </Button>
        </Box>
      </Box>

      <ProductDeleteModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        productId={id}
      />
    </Box>
  );
};

export default ProductDetail;

